// SDK de verificación de Legal (CORP-599). Script externo que monta el trámite
// —documentos, firmantes y contrato— en shadow DOM dentro del modal del panel.
// No tiene SRI porque Legal lo actualiza en cada despliegue (riesgo aceptado).

// ─── Types ────────────────────────────────────────────────────────────────────

export type LegalSdkEnvironment = 'sandbox' | 'production';

/** Contacto que el SDK muestra al cerrarse el trámite o si los datos no coinciden. */
export interface LegalSdkSupport {
  text?: string;
  url?: string;
  urlLabel?: string;
  email?: string;
  phone?: string;
}

export interface LegalSdkError {
  code: string;
  message?: string;
}

export interface LegalSdkMountOptions {
  environment: LegalSdkEnvironment;
  primaryColor: string;
  support?: LegalSdkSupport;
  getToken: () => Promise<string>;
  onLegalId?: (legalId: string) => void;
  onStatusChange?: (status: string, screen?: string) => void;
  onComplete?: (view: unknown) => void;
  onError?: (error: LegalSdkError) => void;
}

export interface LegalSdkHandle {
  unmount: () => void;
}

export interface YummyLegalSdk {
  mount: (
    element: HTMLElement,
    options: LegalSdkMountOptions,
  ) => LegalSdkHandle;
}

declare global {
  interface Window {
    YummyLegal?: YummyLegalSdk;
  }
}

// ─── Config ───────────────────────────────────────────────────────────────────

export const LEGAL_SDK_SCRIPT_URL =
  'https://legal-automations-panel.vercel.app/sdk/v1/legal-verification.js';

/** `brand-500` (tailwind.config.js): botones y acentos del SDK. */
export const LEGAL_SDK_PRIMARY_COLOR = '#007AFF';

/**
 * `REACT_APP_LEGAL_SDK_ENVIRONMENT`: `production` solo en producción. Todo lo
 * demás va a `sandbox`; se pasa siempre explícito al SDK.
 */
export const getLegalSdkEnvironment = (
  value: string | undefined = process.env.REACT_APP_LEGAL_SDK_ENVIRONMENT,
): LegalSdkEnvironment => (value === 'production' ? 'production' : 'sandbox');

/**
 * `REACT_APP_SUPPORT_WHATSAPP`: número con código de país, solo dígitos (los
 * demás caracteres se ignoran). Sin número válido no se manda `support` y el
 * SDK remite al ejecutivo de Yummy.
 */
export const getLegalSdkSupport = (
  value: string | undefined = process.env.REACT_APP_SUPPORT_WHATSAPP,
): LegalSdkSupport | undefined => {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length < 8) return undefined;
  return { url: `https://wa.me/${digits}`, urlLabel: 'WhatsApp' };
};

// ─── Loader ───────────────────────────────────────────────────────────────────

let sdkPromise: Promise<YummyLegalSdk> | null = null;

/**
 * Inyecta el script una sola vez y resuelve con `window.YummyLegal`. Solo se
 * guarda la carga en curso: una vez cargado manda `window.YummyLegal`, y si
 * falla el siguiente llamado vuelve a inyectarlo.
 */
export const loadLegalSdk = (): Promise<YummyLegalSdk> => {
  if (window.YummyLegal) return Promise.resolve(window.YummyLegal);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<YummyLegalSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = LEGAL_SDK_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.YummyLegal) resolve(window.YummyLegal);
      else reject(new Error('LEGAL_SDK_UNAVAILABLE'));
    };
    script.onerror = () => {
      script.remove();
      reject(new Error('LEGAL_SDK_LOAD_FAILED'));
    };
    document.head.appendChild(script);
  }).finally(() => {
    sdkPromise = null;
  });

  return sdkPromise;
};

// ─── First render ─────────────────────────────────────────────────────────────

/** Tope de la espera: pasado esto se muestra lo que haya dibujado el SDK. */
export const LEGAL_SDK_FIRST_RENDER_TIMEOUT_MS = 15000;

/**
 * Avisa cuando el SDK terminó su primera carga. Tras `mount` el SDK muestra su
 * propio «Cargando…» con `aria-busy="true"` en su tarjeta (shadow DOM abierto)
 * y lo pasa a `"false"` al traer el trámite. Esperarlo evita mostrar dos
 * cargas seguidas de distinto tamaño. Si el SDK deja de marcar `aria-busy`,
 * avisa enseguida, y si nunca termina, al tope: no se queda colgado.
 * Devuelve la limpieza.
 */
export const watchLegalSdkFirstRender = (
  container: HTMLElement,
  onRendered: () => void,
  timeoutMs: number = LEGAL_SDK_FIRST_RENDER_TIMEOUT_MS,
): (() => void) => {
  const root: ParentNode = container.shadowRoot ?? container;
  const isBusy = (): boolean =>
    root.querySelector('[aria-busy="true"]') !== null;

  if (!isBusy()) {
    onRendered();
    return () => {};
  }

  let isDone = false;
  const finish = (): void => {
    if (isDone) return;
    isDone = true;
    observer.disconnect();
    window.clearTimeout(timeout);
    onRendered();
  };

  const observer = new MutationObserver(() => {
    if (!isBusy()) finish();
  });
  observer.observe(root as Node, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-busy'],
  });
  const timeout = window.setTimeout(finish, timeoutMs);

  return () => {
    isDone = true;
    observer.disconnect();
    window.clearTimeout(timeout);
  };
};
